import type { HandleProps } from "@xyflow/react";
import { useNodeConnections,useNodeId } from "@xyflow/react";
import type { MouseEvent } from "react";
import { useCallback } from "react";

import { useAddNodeMenu } from "./add-node-menu-context";
import { AddHandleChrome } from "./AddHandleChrome";
import { AddNextHandle, BaseHandle, BaseHiddenHandle, DecisionHiddenHandle } from "./styled/basehandle.styled";

type AddConnectionHandleProps = HandleProps & {
	connectionLimit?: number;
	/** When the + is replaced by a hidden anchor, keep it fully invisible (e.g. decision branches). */
	suppressHiddenHandleReveal?: boolean;
	/**
	 * QA: when true (default), render the larger 24×24 "add next" button
	 * style — visible plus, brand ring, drag-to-connect still wired. When
	 * false, fall back to the small 10×10 BaseHandle dot (used for
	 * connection-only points where the visible button would be distracting).
	 */
	prominent?: boolean;
};

const AddConnectionHandle = ({ onClick, connectionLimit = 1, suppressHiddenHandleReveal, prominent = true, ...props }: AddConnectionHandleProps) => {
	const { openMenu } = useAddNodeMenu();
	const nodeId = useNodeId();
	const connections = useNodeConnections({ handleId: props.id ?? undefined, handleType: "source" });
	const hasConnection = connections.length >= connectionLimit;

	const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
		if (hasConnection) return;

		if (event.currentTarget instanceof HTMLElement) {
			openMenu(event.currentTarget, {
				sourceNodeId: nodeId ?? undefined,
				sourceHandleId: props.id ?? undefined,
			});
		}

		onClick?.(event);
	}, [onClick, openMenu, nodeId, props.id, hasConnection]);

	/**
   * Logic around hiding the connectable handle if it has a connection
   */
	if (hasConnection) {
		const Hidden = suppressHiddenHandleReveal ? DecisionHiddenHandle : BaseHiddenHandle;
		return <Hidden {...props} />;
	}

	// Open the add-node menu via keyboard (Enter/Space) — pointer drag still
	// works for hand-drawn connections. Satisfies WCAG 2.1 AA 2.1.1.
	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		if (hasConnection) return;
		if (event.currentTarget instanceof HTMLElement) {
			openMenu(event.currentTarget, {
				sourceNodeId: nodeId ?? undefined,
				sourceHandleId: props.id ?? undefined,
			});
		}
	};

	const Handle = prominent ? AddNextHandle : BaseHandle;
	return (
		<Handle
			{...props}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			isConnectable
			tabIndex={0}
			role="button"
			aria-label="Connection point — press Enter to add a new connected node, or drag to link to an existing node"
		>
			<AddHandleChrome />
		</Handle>
	);
};

export default AddConnectionHandle;
